import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import Logo from '../components/Logo';
import { ArrowRight } from 'lucide-react';

/** Map known backend `error` strings to i18n keys (backend messages are English). */
function translateLoginApiError(raw, t) {
  if (raw == null || raw === '') return t('login.errorFailed');
  const keyByMessage = {
    'Invalid credentials': 'login.invalidCredentials',
    'Email and password are required': 'login.apiEmailPasswordRequired',
    'Tenant not found': 'login.apiTenantNotFound',
    'Invalid subdomain': 'login.apiInvalidSubdomain',
  };
  const key = keyByMessage[String(raw).trim()];
  return key ? t(key) : String(raw);
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) {
      return t('login.emailRequired');
    }
    if (!emailRegex.test(emailValue)) {
      return t('login.emailInvalid');
    }
    return '';
  };

  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      return t('login.passwordRequired');
    }
    if (passwordValue.length < 8) {
      return t('login.passwordMinLength');
    }
    if (!/[A-Z]/.test(passwordValue)) {
      return t('login.passwordUppercase');
    }
    if (!/[0-9]/.test(passwordValue)) {
      return t('login.passwordNumber');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordValue)) {
      return t('login.passwordSpecial');
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    
    if (emailErr || passwordErr) {
      return;
    }

    setLoading(true);

    try {
      await login(email, password);
      navigate('/User_page');
    } catch (err) {
      const apiErr = err.response?.data?.error ?? err.response?.data?.message;
      const status = err.response?.status;
      const isNetworkError =
        err.message === 'Network Error' ||
        (err.response === undefined && String(err.message || '').includes('Network'));
      const hint = isNetworkError ? t('login.networkHint') : '';

      let display;
      if (apiErr != null && String(apiErr).trim() !== '') {
        display = translateLoginApiError(apiErr, t);
      } else if (status === 401) {
        display = t('login.invalidCredentials');
      } else if (err.response) {
        display = t('login.errorFailed');
      } else {
        display = t('login.errorFailed');
      }
      setError(display + hint);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFFFF', color: '#1B211A' }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ borderBottom: '1px solid rgba(169, 201, 232, 0.3)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
<div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between gap-4">
            <Logo asLink to="/" />
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            <Link 
              to="/" 
              className="text-xs md:text-sm transition-colors hover:opacity-80"
              style={{ color: 'rgb(100, 130, 160)' }}
            >
              {t('common.backToHome')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={{ color: '#1B211A' }}>
              {t('login.title')}
            </h1>
            <p className="text-sm" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('login.subtitle')}
            </p>
          </div>

          <div className="rounded-2xl p-6 md:p-8" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(169, 201, 232, 0.3)' }}>
            {error && (
              <div className="mb-4 p-3 rounded-lg text-xs" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                  {t('login.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={{
                    border: emailError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                    backgroundColor: '#FFFFFF',
                    color: '#1B211A',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = emailError ? '#dc2626' : 'rgb(169, 201, 232)';
                  }}
                  onBlur={(e) => {
                    const err = validateEmail(email);
                    setEmailError(err);
                    e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                  }}
                />
                {emailError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {emailError}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                  {t('login.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={{
                    border: passwordError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                    backgroundColor: '#FFFFFF',
                    color: '#1B211A',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = passwordError ? '#dc2626' : 'rgb(169, 201, 232)';
                  }}
                  onBlur={(e) => {
                    const err = validatePassword(password);
                    setPasswordError(err);
                    e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                  }}
                />
                {passwordError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {passwordError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: loading ? 'rgb(100, 130, 160)' : 'rgb(169, 201, 232)',
                  color: '#FFFFFF',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? t('login.loggingIn') : (
                  <>
                    {t('login.submit')}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs mt-6" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('login.noAccount')}{' '}
              <Link 
                to="/register" 
                className="font-medium hover:opacity-80 transition-opacity"
                style={{ color: 'rgb(169, 201, 232)' }}
              >
                {t('login.registerHere')}
              </Link>
            </p>
            {(import.meta.env.VITE_BUILD_ID && import.meta.env.MODE === 'production') && (
              <p className="text-center text-[10px] mt-4 opacity-60" style={{ color: 'rgb(100, 130, 160)' }} title={t('login.buildIdTitle')}>
                Build: {import.meta.env.VITE_BUILD_ID}
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Login;
